import { Router } from "@solidjs/router";
import { FileRoutes } from "@solidjs/start/router";
import { Accessor, createMemo, ErrorBoundary, Suspense } from "solid-js";
import { DottedLoader } from "./widgets/loader";
import "./app.css";
import { ModalProvider } from "~/components/modal";
import { HubbleError } from "~/components/error";
import { MetaProvider } from "@solidjs/meta";
import { isServer, getRequestEvent } from "solid-js/web";
import "solid-devtools";
import { captureErrorInSentry } from "~/utils/third_party/sentry";
import { config } from "~/data/config";
import { Toaster } from "solid-toast";
import { HttpHeader } from "@solidjs/start";
import { GoogleAnalytics } from "./utils/third_party/google_analytics";
import { AnalyticsServiceProvider } from "./context/analytics_service_context";
import HubbleAnalyticsService from "./services/analytics_service/hubble_analytics_service";
import { VersionUpdateMessage } from "./components/global/version_update_message";
import { TelescopeProvider } from "@hubble/telescope";
import { ThemeToggleButton } from "./components/global/theme_toggle_button";
import ClientOnlyComponent, {
  ClientComponent,
} from "./client_only_components/client_component";
import {
  clientIdConfig,
  getBrandColor,
  getMomentsV3PartnerConfig,
} from "./data/partner_config";
import { withSentryRouterRouting } from "@sentry/solidstart/solidrouter";

import { getOverridenTelescopeComponents } from "./data/partner_config";
import { getCookie } from "./utils/client_cookie";
import { Cookie, Mode } from "./types";

const SentryRouter = withSentryRouterRouting(Router);

export default function HubbleRouter() {
  // initVersionCheck(); //dummy comment 7

  const requestEvent = getRequestEvent();
  // const clientId = requestEvent?.locals.clientId;
  const getClientId = () => {
    if (isServer) {
      return requestEvent?.locals.clientId;
    }
    return getCookie(Cookie.ClientId);
  };

  const getMode = createMemo(() => {
    if (isServer) {
      return (requestEvent?.locals.mode ?? "hubble") as Mode;
    }
    return (getCookie(Cookie.Mode) ?? "hubble") as Mode;
  });

  const getAnalyticsService = () => {
    return new HubbleAnalyticsService({
      defaultProperties: {
        clientId: getClientId(),
        mode: getMode(),
      },
    });
  };

  const getTheme = () => {
    // todo: remove this list from here after all the clients are migrated to the new theme
    const darkThemeClientIds = [
      clientIdConfig.tdClientId,
      clientIdConfig.moneycontrolClientId,
      clientIdConfig.rocheClientId,
      clientIdConfig.bajajClientId,
      clientIdConfig.godrejPropertiesClientId,
    ];
    if (darkThemeClientIds.includes(getClientId())) {
      return "telescope-dark-theme";
    }
    return getMomentsV3PartnerConfig(getClientId()).theme;
  };

  const _getBrandColor = () => {
    return getBrandColor(getClientId() ?? "");
  };

  return (
    <SentryRouter
      root={(props) => (
        <>
          <ErrorBoundary
            fallback={(err) => {
              if (!isServer) {
                captureErrorInSentry(err);
              }

              if (config.env !== "Prod") {
                console.trace(err);
              }

              // Check if this is a deployment error
              // this is a temporary fix to show the version update message
              // when the user is navigating after a new deployment
              const isFreshDeploymentError = err.message
                ?.toLowerCase()
                .includes("failed to fetch dynamically imported module");

              if (isFreshDeploymentError) {
                return <VersionUpdateMessage />;
              }

              return <HubbleError errorMessage={err.message} />;
            }}
          >
            <AnalyticsServiceProvider analyticsService={getAnalyticsService()}>
              <TelescopeProvider
                theme={getTheme()}
                components={getOverridenTelescopeComponents({
                  clientId: getClientId(),
                  mode: getMode(),
                })}
                styles={
                  _getBrandColor()
                    ? {
                        "--brand-tbd-base": _getBrandColor(),
                      }
                    : {}
                }
              >
                <ModalProvider>
                  <MetaProvider>
                    <SecurityHeaders />
                    <GoogleAnalytics />
                    <Suspense fallback={<DottedLoader from="app.tsx" />}>
                      {props.children}
                      {/* <VersionUpdateModal /> */}
                    </Suspense>
                  </MetaProvider>
                  <ClientOnlyComponent component={ClientComponent.ModalHost} />
                  <ClientOnlyComponent
                    component={ClientComponent.SnackbarHost}
                  />
                  <Toaster position="top-center" />
                  {/* {config.env === "Staging" &&
                    clientIdConfig.indriyaClientId !== getClientId() &&
                    clientIdConfig.axisMaxLifeClientId !== getClientId() &&
                    clientIdConfig.mastercardIndriyaClientId !==
                      getClientId() && <ThemeToggleButton />} */}
                </ModalProvider>
              </TelescopeProvider>
            </AnalyticsServiceProvider>
          </ErrorBoundary>
        </>
      )}
    >
      <FileRoutes />
    </SentryRouter>
  );
}

function SecurityHeaders() {
  return (
    <>
      {/* any domains for allowing iframes should be whitelisted in below header (csp) */}
      <HttpHeader
        name="Content-Security-Policy"
        value="frame-ancestors 'self' https://web.getvisitapp.net vouchers.myhubble.money sdk.myhubble.money sdk.dev.myhubble.money vouchers.dev.myhubble.money http://localhost:* https://hubble-sdk-git-fix-rewards-portal-deep-link-b4a76d-hubble-money.vercel.app"
      />

      <HttpHeader name="X-Content-Type-Options" value="nosniff" />
    </>
  );
}
